// The eBay header layout (Lab-only experiment, docs/decisions.md 2026-10-07):
// eBay conversations get the Website header's look while keeping their eBay
// data. One flag decides it, so App.jsx never scatters channel checks.
// Website conversations (trqClientCode 1AW / AMW) are unchanged.
import { ebayListingUrl } from './ebayLinks.js'
import { headerChannelFor } from '@cxhub/shared'

// TRQ order status -> text color, keyed on the lowercased status. Shared by
// the Website status line and the eBay layout's Status / Total line.
export const ORDER_STATUS_COLOR = {
  shipped: 'success.main',
  cancelled: 'error.main',
  delivered: 'teal.main',
  open: 'primary.main',
}

// An m365_email conversation whose client is eBay (1AE / AME): a Sales Hold
// eBay email, an automated return notice, any other eBay-client email.
export function isEbayClientEmail(conversation) {
  return conversation?.channel === 'm365_email'
    && (conversation.client === 'eBay' || ['1AE', 'AME'].includes(conversation.trqClientCode))
}

// The new eBay layout: the eBay channel, or an eBay-client email, or a task
// contact whose order is eBay's (headerChannelFor(), 2026-10-13). A Website
// order (1AW / AMW) or a Walmart one (WMT) never gets it, whatever the
// channel says.
export function isNewEbayLayout(conversation) {
  if (['1AW', 'AMW', 'WMT'].includes(conversation?.trqClientCode)) return false
  return headerChannelFor(conversation) === 'ebay' || isEbayClientEmail(conversation)
}

// Box 2's top line: Status on the left (colored), Order Total on the right.
// null when the order carries neither.
export function orderStatusTotal(orderSummary) {
  const status = orderSummary?.status ?? null
  const total = orderSummary?.orderTotal ?? null
  if (status == null && total == null) return null
  return {
    status,
    statusColor: status != null ? (ORDER_STATUS_COLOR[String(status).toLowerCase()] ?? null) : null,
    total,
  }
}

// An ordered item's own eBay listing page, from its TRQ ebay_id
// (item.ebayItemId). null when the item carries none — never the
// conversation's own listing.
export function ebayItemUrl(ebayItemId) {
  if (ebayItemId == null || String(ebayItemId).trim() === '') return null
  return `https://www.ebay.com/itm/${encodeURIComponent(String(ebayItemId).trim())}`
}

// The "Initiated Conversation From Listing" banner: the eBay channel only,
// and only when the conversation came from a listing. Email carries no
// listing reference.
export function listingBannerFor(conversation) {
  if (conversation?.channel !== 'ebay') return null
  if (conversation.referenceType !== 'LISTING' || !conversation.referenceId) return null
  const listingId = String(conversation.referenceId)
  return { listingId, url: ebayListingUrl(listingId) }
}

const normId = (v) => String(v ?? '').trim().toLowerCase()

// Whether the "Initiated Conversation From Listing" banner shows (Lab-only,
// 2026-10-08): only when the listing is NOT one of the ordered parts, or when
// no order is identified. With an order, the banner waits for its items (no
// flash), and shows when they fail to load or there are none (fail toward
// showing the agent the listing). A part matches when its eBay listing id
// (ebayItemId, from TRQ's ebay_id) is the conversation's listing, or its SKU
// or AM SKU is the listing's resolved SKU (case and spacing aside).
//   listingId: listingBannerFor()'s listingId (null: not a listing conversation)
//   itemsState: 'loading' | 'loaded' | 'error'
export function shouldShowListingBanner({ hasOrder, itemsState, orderItems, listingId, listingSku = null }) {
  if (!listingId) return false
  if (!hasOrder) return true
  if (itemsState === 'loading') return false
  if (itemsState === 'error') return true
  const items = Array.isArray(orderItems) ? orderItems : []
  if (!items.length) return true
  const id = normId(listingId)
  const sku = normId(listingSku)
  const matches = items.some((it) =>
    (it?.ebayItemId != null && normId(it.ebayItemId) === id)
    || (sku && (normId(it?.sku) === sku || normId(it?.amSku) === sku)))
  return !matches
}
